import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Check,
  X,
  Sparkles,
  Apple,
  ChevronRight,
  Shield,
  Cpu,
  Zap,
  Terminal,
  Code2,
  Github,
  ExternalLink,
  Star,
  Hammer,
  Lock,
  Server,
  Gauge,
  Globe2,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";

/* ----------------------- DATA ----------------------- */

// Tiny inline platform glyphs (kept consistent with sidebar lucide style)
const Win = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M0 3.45L9.8 2.1v9.4H0V3.45zM10.95 1.95L24 0v11.5H10.95V1.95zM0 12.5h9.8v9.4L0 20.55V12.5zM10.95 12.5H24V24l-13.05-1.95V12.5z" />
  </svg>
);
const Linux = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.077 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.99.99 0 00-.105.121c-.7.97-1.117 1.952-1.39 2.59-.234.66-.226 1.31-.04 1.96.18.62.5 1.18.94 1.66.42.51 1.05.92 1.78 1.12.7.21 1.4.21 2.08-.07.49-.2.94-.51 1.36-.94.6.07 1.18-.04 1.71-.27.84-.36 1.41-.99 1.7-1.79.13-.35.19-.71.18-1.06.32-.04.64-.13.95-.25.31-.12.6-.28.86-.46l.04-.03c.84.74 1.78 1.27 2.74 1.45 1.13.21 2.22-.08 3.05-.85.81-.74 1.32-1.82 1.32-3.03 0-.86-.27-1.71-.71-2.45-.45-.78-1.06-1.45-1.7-2.05-.51-.49-.79-.94-.97-1.43-.19-.5-.27-1.04-.27-1.66 0-.43.04-.91.04-1.46 0-.81-.06-1.91-.51-2.87-.23-.48-.55-.92-.97-1.27-.42-.34-.94-.59-1.55-.7-.42-.07-.83-.08-1.22-.04-.81-.4-1.66-.6-2.52-.6z" />
  </svg>
);

const RECOMMENDED = "qbittorrent";

const CLIENTS = [
  {
    id: "qbittorrent",
    name: "qBittorrent",
    tagline: "The reliable, ad-free favourite",
    accent: "#2D6CB0",
    site: "https://www.qbittorrent.org/",
    summary:
      "Open source, ad-free, and the de-facto recommendation across the community. Native built-in search, RSS auto-download, sequential streaming, and a feature parity that rivals any commercial client.",
    platforms: { windows: true, mac: true, linux: true },
    downloads: {
      windows: "https://www.qbittorrent.org/download#windows",
      mac: "https://www.qbittorrent.org/download#macos",
      linux: "https://www.qbittorrent.org/download#linux",
    },
    features: {
      ads: false,
      magnet: true,
      sequential: true,
      streaming: true,
      rss: true,
      remoteWeb: true,
      vpnBind: true,
      headless: true,
      ipFilter: true,
      scheduler: true,
    },
    pros: [
      "Completely free, ad-free, open-source",
      "Built-in search engine across multiple sites",
      "VPN/interface binding (kill-switch friendly)",
    ],
    cons: ["UI is functional, not flashy", "Initial setup of search plugins is manual"],
    badge: "Editor's Pick",
    recommendedFor: "Everyone — beginners to power users",
  },
  {
    id: "transmission",
    name: "Transmission",
    tagline: "Lightweight. Native. Beautiful.",
    accent: "#D80B0B",
    site: "https://transmissionbt.com/",
    summary:
      "Famously the lightest mainstream client. A native macOS app, a tidy GTK build, and a slick web UI on Linux servers. Ideal when you want a torrent client to disappear into the background.",
    platforms: { windows: true, mac: true, linux: true },
    downloads: {
      windows: "https://transmissionbt.com/download",
      mac: "https://transmissionbt.com/download",
      linux: "https://transmissionbt.com/download",
    },
    features: {
      ads: false,
      magnet: true,
      sequential: false,
      streaming: false,
      rss: false,
      remoteWeb: true,
      vpnBind: true,
      headless: true,
      ipFilter: true,
      scheduler: true,
    },
    pros: [
      "Tiny memory & CPU footprint",
      "Daemon mode is industry standard for seedboxes",
      "Polished native macOS interface",
    ],
    cons: ["No built-in search", "No native RSS (extension required)"],
    recommendedFor: "Seedboxes, mac users, minimalists",
  },
  {
    id: "deluge",
    name: "Deluge",
    tagline: "The extensible swiss army knife",
    accent: "#1A75D2",
    site: "https://deluge-torrent.org/",
    summary:
      "A client/daemon architecture with a thriving plugin ecosystem — sequential download, label-based automation, Plex/Sonarr integration. If you can dream it, there's probably a Deluge plugin for it.",
    platforms: { windows: true, mac: true, linux: true },
    downloads: {
      windows: "https://dev.deluge-torrent.org/wiki/Download",
      mac: "https://dev.deluge-torrent.org/wiki/Download",
      linux: "https://dev.deluge-torrent.org/wiki/Download",
    },
    features: {
      ads: false,
      magnet: true,
      sequential: true,
      streaming: true,
      rss: true,
      remoteWeb: true,
      vpnBind: true,
      headless: true,
      ipFilter: true,
      scheduler: true,
    },
    pros: [
      "Massive plugin ecosystem",
      "Daemon + thin client = perfect for headless setups",
      "Excellent label/automation system",
    ],
    cons: ["Plugin install can feel old-school", "macOS build lags behind"],
    recommendedFor: "Tinkerers, *arr-stack users, automators",
  },
  {
    id: "biglybt",
    name: "BiglyBT",
    tagline: "Power-user playground",
    accent: "#7E3FB5",
    site: "https://www.biglybt.com/",
    summary:
      "The successor to Vuze, BiglyBT exposes every dial of the BitTorrent protocol — swarm merging, peer banning, advanced trackers, I2P anonymity. Overkill for casual use; godlike for those who want control.",
    platforms: { windows: true, mac: true, linux: true },
    downloads: {
      windows: "https://www.biglybt.com/download",
      mac: "https://www.biglybt.com/download",
      linux: "https://www.biglybt.com/download",
    },
    features: {
      ads: false,
      magnet: true,
      sequential: true,
      streaming: true,
      rss: true,
      remoteWeb: true,
      vpnBind: true,
      headless: true,
      ipFilter: true,
      scheduler: true,
    },
    pros: [
      "Every protocol-level option exposed",
      "Built-in I2P plugin for anonymous swarms",
      "Swarm merging speeds up rare files",
    ],
    cons: ["Java-based — heavier on RAM", "UI is dense and intimidating"],
    recommendedFor: "Power users who love knobs and dials",
  },
  {
    id: "rtorrent",
    name: "rTorrent + ruTorrent",
    tagline: "Terminal-first, web-second",
    accent: "#22C77A",
    site: "https://github.com/rakshasa/rtorrent",
    summary:
      "The seedbox legend. A blazing-fast C++ daemon controlled via ncurses or the ruTorrent web UI. Sips RAM, eats whole gigabit lines for breakfast. The choice of every serious seedbox provider on earth.",
    platforms: { windows: false, mac: true, linux: true },
    downloads: {
      mac: "https://github.com/rakshasa/rtorrent#installation",
      linux: "https://github.com/rakshasa/rtorrent#installation",
    },
    features: {
      ads: false,
      magnet: true,
      sequential: false,
      streaming: false,
      rss: true,
      remoteWeb: true,
      vpnBind: true,
      headless: true,
      ipFilter: true,
      scheduler: true,
    },
    pros: [
      "Tiny resource use, huge throughput",
      "ruTorrent web UI is feature-complete",
      "Default on virtually every seedbox provider",
    ],
    cons: ["No first-class Windows build", "Setup is for the technically curious"],
    recommendedFor: "Self-hosters, seedbox owners",
  },
  {
    id: "utorrent",
    name: "μTorrent Classic",
    tagline: "The OG — handle with care",
    accent: "#7BB72B",
    site: "https://www.utorrent.com/",
    summary:
      "The client that put BitTorrent on the map. Still lightweight on Windows, but the modern installer includes ads and bundled offers. We list it for completeness — most of the community has moved on.",
    platforms: { windows: true, mac: true, linux: false },
    downloads: {
      windows: "https://www.utorrent.com/desktop/",
      mac: "https://www.utorrent.com/desktop/",
    },
    features: {
      ads: true,
      magnet: true,
      sequential: false,
      streaming: true,
      rss: true,
      remoteWeb: true,
      vpnBind: false,
      headless: false,
      ipFilter: true,
      scheduler: true,
    },
    pros: ["Tiny binary size", "Brand familiarity for newcomers"],
    cons: [
      "Bundled ads & sponsored offers",
      "Closed source",
      "Past EULA / crypto-miner controversies",
    ],
    recommendedFor: "Legacy users only — prefer qBittorrent",
  },
];

const FEATURE_ROWS = [
  { key: "ads", label: "Ad-free", invert: true },
  { key: "magnet", label: "Magnet links" },
  { key: "sequential", label: "Sequential download" },
  { key: "streaming", label: "Stream while downloading" },
  { key: "rss", label: "RSS auto-download" },
  { key: "remoteWeb", label: "Remote / Web UI" },
  { key: "vpnBind", label: "VPN / interface binding" },
  { key: "headless", label: "Headless daemon" },
  { key: "ipFilter", label: "IP filter / blocklists" },
  { key: "scheduler", label: "Bandwidth scheduler" },
];

/* ----------------------- PARTNERS ----------------------- */

const PARTNERS = {
  vpn: [
    {
      id: "mullvad",
      name: "Mullvad VPN",
      kicker: "Privacy-first VPN",
      accent: "#FFD700",
      Icon: Lock,
      summary:
        "Flat €5/month. No email required. WireGuard, port forwarding via SOCKS5, audited and open-source clients.",
      bullets: ["Anonymous accounts", "P2P on every server", "Pay in cash or crypto"],
      cta: "Get Mullvad",
      url: "https://mullvad.net/",
    },
    {
      id: "proton",
      name: "Proton VPN",
      kicker: "Big network, Swiss law",
      accent: "#6D4AFF",
      Icon: Globe2,
      summary:
        "From the makers of ProtonMail. Free tier available, Secure Core multi-hop on Plus, port forwarding on dedicated P2P servers.",
      bullets: ["Free tier (no P2P)", "Secure Core multi-hop", "Audited no-logs policy"],
      cta: "Get Proton VPN",
      url: "https://protonvpn.com/",
    },
  ],
  seedbox: [
    {
      id: "dediseedbox",
      name: "DediSeedbox",
      kicker: "Dedicated seedboxes",
      accent: "#2BD9A5",
      Icon: Server,
      summary:
        "Dedicated and shared seedboxes in the Netherlands with up to 20Gbps uplinks. One-click rTorrent, qBittorrent, Deluge, Plex and the *arr stack — built for the power users who outgrew shared plans.",
      bullets: ["Up to 20Gbps", "Dedicated boxes", "Plex / *arr ready"],
      cta: "Browse DediSeedbox",
      url: "https://dediseedbox.com/clients/aff.php?aff=1356",
    },
    {
      id: "rapidseedbox",
      name: "RapidSeedbox",
      kicker: "Beginner-friendly seedbox",
      accent: "#FF6B35",
      Icon: Gauge,
      summary:
        "One-click install of qBittorrent, Deluge, Plex, Jellyfin. SSD storage, free VPN included on most plans.",
      bullets: ["NVMe storage", "Free VPN bundled", "24/7 live support"],
      cta: "Compare RapidSeedbox",
      url: "https://members.rapidseedbox.com/aff.php?aff=1222",
    },
  ],
};

/* ----------------------- COMPONENTS ----------------------- */

function PlatformBadge({ kind }) {
  const map = {
    windows: { Icon: Win, label: "Windows" },
    mac: { Icon: Apple, label: "macOS" },
    linux: { Icon: Linux, label: "Linux" },
  };
  const { Icon, label } = map[kind];
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-white/5 border border-white/10 text-[var(--tt-text-muted)]">
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function DownloadButtons({ client }) {
  const entries = [
    ["windows", "Windows", Win],
    ["mac", "macOS", Apple],
    ["linux", "Linux", Linux],
  ].filter(([k]) => client.downloads[k]);
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([k, label, Icon]) => (
        <a
          key={k}
          href={client.downloads[k]}
          target="_blank"
          rel="noreferrer noopener"
          data-testid={`software-download-${client.id}-${k}`}
          className="group inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold bg-white/5 border border-white/10 hover:border-[var(--tt-primary)]/60 hover:bg-[rgba(255,42,109,0.08)] text-white/90 transition-colors"
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
          <Download className="w-3 h-3 opacity-50 group-hover:opacity-100 group-hover:translate-y-px transition-all" />
        </a>
      ))}
    </div>
  );
}

function ClientLogo({ client, size = 56 }) {
  // Stylised mark — first letter, brand accent, glassy
  return (
    <div
      className="relative flex-shrink-0 rounded-2xl flex items-center justify-center font-display font-extrabold select-none overflow-hidden"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${client.accent}33, transparent 60%), #0b0b0b`,
        boxShadow: `inset 0 0 0 1px ${client.accent}55, 0 8px 24px ${client.accent}22`,
      }}
    >
      <span
        className="text-2xl"
        style={{
          color: client.accent,
          textShadow: `0 0 18px ${client.accent}99`,
        }}
      >
        {client.name[0] === "μ" ? "μ" : client.name[0].toUpperCase()}
      </span>
      <span
        className="absolute -bottom-3 -right-3 w-10 h-10 rounded-full opacity-30 blur-xl"
        style={{ background: client.accent }}
      />
    </div>
  );
}

function FeatureCell({ value, invert }) {
  // For "Ad-free" — invert: false-in-data == ad-free (good)
  const positive = invert ? !value : !!value;
  return positive ? (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--tt-primary)]/15 border border-[var(--tt-primary)]/30">
      <Check className="w-3.5 h-3.5 text-[var(--tt-primary)]" strokeWidth={3} />
    </span>
  ) : (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/[0.03] border border-white/10">
      <X className="w-3.5 h-3.5 text-white/30" strokeWidth={2.5} />
    </span>
  );
}

function ClientCard({ client, featured = false }) {
  return (
    <article
      data-testid={`software-card-${client.id}`}
      className={`relative rounded-3xl border bg-[var(--tt-surface)]/70 backdrop-blur-md overflow-hidden transition-all hover:-translate-y-0.5 ${
        featured
          ? "border-[var(--tt-primary)]/40 shadow-[0_0_0_1px_rgba(255,42,109,0.25)]"
          : "border-[var(--tt-border)] hover:border-[var(--tt-primary)]/30"
      }`}
    >
      {/* tinted halo */}
      <div
        className="absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-40 blur-3xl pointer-events-none"
        style={{ background: client.accent }}
      />

      <div className="relative p-5 md:p-6">
        <div className="flex items-start gap-4">
          <ClientLogo client={client} size={featured ? 72 : 56} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="font-display text-xl md:text-2xl font-extrabold tracking-tight">
                {client.name}
              </h3>
              {client.badge && (
                <span
                  className="text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded"
                  style={{
                    color: "var(--tt-yellow)",
                    background: "rgba(255,215,0,0.12)",
                    border: "1px solid rgba(255,215,0,0.3)",
                  }}
                >
                  {client.badge}
                </span>
              )}
            </div>
            <div className="text-xs md:text-sm text-[var(--tt-text-muted)] italic mb-2">
              {client.tagline}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {client.platforms.windows && <PlatformBadge kind="windows" />}
              {client.platforms.mac && <PlatformBadge kind="mac" />}
              {client.platforms.linux && <PlatformBadge kind="linux" />}
            </div>
          </div>
        </div>

        <p className="text-sm text-white/80 leading-relaxed mt-4">{client.summary}</p>

        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-[var(--tt-primary)] mb-2 flex items-center gap-1.5">
              <Check className="w-3 h-3" /> Strengths
            </div>
            <ul className="space-y-1.5 text-xs text-white/85">
              {client.pros.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <ChevronRight className="w-3 h-3 mt-0.5 text-[var(--tt-primary)] flex-shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-white/40 mb-2 flex items-center gap-1.5">
              <X className="w-3 h-3" /> Trade-offs
            </div>
            <ul className="space-y-1.5 text-xs text-white/55">
              {client.cons.map((c) => (
                <li key={c} className="flex items-start gap-2">
                  <ChevronRight className="w-3 h-3 mt-0.5 text-white/30 flex-shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
          <div className="text-[11px] text-[var(--tt-text-muted)] font-mono-tt">
            <span className="text-white/40">recommended for </span>
            <span className="text-white/80">{client.recommendedFor}</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={client.site}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`software-site-${client.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-[var(--tt-text-muted)] hover:text-white transition-colors"
            >
              Site <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        <div className="mt-4">
          <DownloadButtons client={client} />
        </div>
      </div>
    </article>
  );
}

/* ----------------------- PAGE ----------------------- */

function PartnerCard({ partner }) {
  const { Icon } = partner;
  const handleClick = () => {
    // Fire-and-forget tracking — never block the user navigating
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/partners/click`;
      const body = JSON.stringify({ partner_id: partner.id });
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      /* ignore */
    }
  };
  return (
    <a
      href={partner.url}
      target="_blank"
      rel="noreferrer noopener sponsored"
      data-testid={`partner-card-${partner.id}`}
      onClick={handleClick}
      onAuxClick={handleClick}
      className="group relative rounded-3xl border border-[var(--tt-border)] bg-[var(--tt-surface)]/70 backdrop-blur-md overflow-hidden p-5 md:p-6 block transition-all hover:-translate-y-0.5 hover:border-[var(--tt-primary)]/40"
    >
      <div
        className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-30 blur-3xl pointer-events-none transition-opacity group-hover:opacity-50"
        style={{ background: partner.accent }}
      />
      <div className="relative">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${partner.accent}33, transparent 60%), #0b0b0b`,
              boxShadow: `inset 0 0 0 1px ${partner.accent}55`,
            }}
          >
            <Icon className="w-5 h-5" style={{ color: partner.accent }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: partner.accent }}>
              {partner.kicker}
            </div>
            <div className="font-display text-xl font-extrabold tracking-tight mt-0.5">
              {partner.name}
            </div>
          </div>
        </div>

        <p className="text-sm text-white/80 leading-relaxed mt-4">{partner.summary}</p>

        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
          {partner.bullets.map((b) => (
            <li
              key={b}
              className="text-[11px] text-white/70 px-2.5 py-1.5 rounded-full bg-white/5 border border-white/10 flex items-center gap-1.5"
            >
              <Check className="w-3 h-3 text-[var(--tt-primary)] flex-shrink-0" />
              <span className="truncate">{b}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">
            Affiliate partner
          </span>
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
            style={{
              color: partner.accent,
              borderColor: `${partner.accent}55`,
              background: `${partner.accent}14`,
            }}
          >
            {partner.cta}
            <ExternalLink className="w-3 h-3" />
          </span>
        </div>
      </div>
    </a>
  );
}

export default function SoftwarePage() {
  const [filter, setFilter] = useState("all"); // all | windows | mac | linux

  useSeo({
    title: "Best BitTorrent Clients 2026 — qBittorrent, Transmission, Deluge & more | TorHub",
    description:
      "An honest, ad-free comparison of the best desktop BitTorrent clients in 2026 — qBittorrent, Transmission, Deluge, BiglyBT, rTorrent and μTorrent. Feature matrix, pros and cons, and direct downloads for Windows, macOS and Linux.",
    path: "/software",
  });

  const filtered =
    filter === "all"
      ? CLIENTS
      : CLIENTS.filter((c) => c.platforms[filter]);

  const featured = CLIENTS.find((c) => c.id === RECOMMENDED);
  const others = filtered.filter((c) => c.id !== RECOMMENDED);

  const filterButtons = [
    { key: "all", label: "All platforms", Icon: Sparkles },
    { key: "windows", label: "Windows", Icon: Win },
    { key: "mac", label: "macOS", Icon: Apple },
    { key: "linux", label: "Linux", Icon: Linux },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />
        <main className="px-4 md:px-10 py-8 flex-1 w-full max-w-6xl mx-auto">
          <Link
            to="/"
            data-testid="software-back-home"
            className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-2 text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          {/* HERO */}
          <header className="relative mb-12">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--tt-primary)] font-bold mb-3">
              <Hammer className="w-3.5 h-3.5" />
              Tools / BitTorrent Clients
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95] tracking-tight max-w-3xl">
              You found the magnet.
              <br />
              <span className="text-[var(--tt-primary)]">Now pick the engine.</span>
            </h1>
            <p className="text-sm md:text-base text-[var(--tt-text-muted)] mt-5 max-w-2xl leading-relaxed">
              A magnet link is useless without a BitTorrent client to chew through it. Below is a
              hand-picked, ad-free, opinionated list of desktop clients the TorHub community actually
              uses — compared honestly, with downloads for every major OS.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {filterButtons.map(({ key, label, Icon }) => {
                const active = filter === key;
                return (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    data-testid={`software-filter-${key}`}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold transition-all border ${
                      active
                        ? "bg-[var(--tt-primary)] text-black border-[var(--tt-primary)]"
                        : "bg-white/5 text-white/80 border-white/10 hover:border-[var(--tt-primary)]/50 hover:text-white"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                );
              })}
            </div>
          </header>

          {/* QUICK STATS RIBBON */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
            {[
              { Icon: Shield, label: "Ad-free options", value: "5 of 6" },
              { Icon: Cpu, label: "Lowest RAM use", value: "Transmission" },
              { Icon: Zap, label: "Best all-rounder", value: "qBittorrent" },
              { Icon: Terminal, label: "Headless ready", value: "5 of 6" },
            ].map(({ Icon, label, value }) => (
              <div
                key={label}
                className="rounded-2xl bg-[var(--tt-surface)]/60 border border-[var(--tt-border)] backdrop-blur-md px-4 py-3"
              >
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] font-semibold">
                  <Icon className="w-3 h-3" />
                  {label}
                </div>
                <div className="font-display text-lg font-extrabold text-white mt-1">{value}</div>
              </div>
            ))}
          </section>

          {/* FEATURED */}
          {filter === "all" && featured && (
            <section className="mb-12">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--tt-yellow)] font-bold mb-3">
                <Star className="w-3.5 h-3.5" />
                Editor&apos;s Pick
              </div>
              <ClientCard client={featured} featured />
            </section>
          )}

          {/* GRID */}
          <section className="mb-16">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">
                {filter === "all" ? "Alternatives" : "Matching clients"}
              </h2>
              <span className="text-xs text-[var(--tt-text-muted)] font-mono-tt">
                {others.length} {others.length === 1 ? "client" : "clients"}
              </span>
            </div>
            <div className="grid lg:grid-cols-2 gap-5" data-testid="software-grid">
              {(filter === "all" ? others : filtered).map((c) => (
                <ClientCard key={c.id} client={c} />
              ))}
            </div>
            {filtered.length === 0 && (
              <div className="text-center text-[var(--tt-text-muted)] py-12 text-sm border border-[var(--tt-border)] rounded-2xl bg-[var(--tt-surface)]">
                No clients match that platform.
              </div>
            )}
          </section>

          {/* COMPARISON TABLE */}
          <section className="mb-16">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--tt-primary)] font-bold mb-3">
              <Code2 className="w-3.5 h-3.5" />
              Feature-by-feature
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mb-5">
              The honest comparison
            </h2>

            <div
              className="rounded-3xl border border-[var(--tt-border)] bg-[var(--tt-surface)]/70 backdrop-blur-md overflow-hidden"
              data-testid="software-comparison-table"
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="bg-black/40 border-b border-[var(--tt-border)]">
                      <th className="text-left text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] font-bold px-5 py-4">
                        Feature
                      </th>
                      {CLIENTS.map((c) => (
                        <th
                          key={c.id}
                          className="text-center text-[10px] uppercase tracking-wider font-bold px-3 py-4"
                          style={{ color: c.accent }}
                        >
                          {c.name.replace("μTorrent Classic", "μTorrent").replace(" + ruTorrent", "")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {FEATURE_ROWS.map((row, idx) => (
                      <tr
                        key={row.key}
                        className={`border-b border-white/5 ${idx % 2 === 0 ? "bg-white/[0.015]" : ""}`}
                      >
                        <td className="text-left text-white/85 font-medium px-5 py-3">
                          {row.label}
                        </td>
                        {CLIENTS.map((c) => (
                          <td key={c.id} className="text-center px-3 py-3">
                            <FeatureCell value={c.features[row.key]} invert={row.invert} />
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="bg-black/30">
                      <td className="text-left text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] font-bold px-5 py-3">
                        Download
                      </td>
                      {CLIENTS.map((c) => (
                        <td key={c.id} className="text-center px-3 py-3">
                          <a
                            href={c.site}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid={`software-table-link-${c.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--tt-primary)] hover:text-white transition-colors"
                          >
                            Visit <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* PARTNERS — affiliate slot */}
          <section className="mb-12" data-testid="software-partners">
            <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--tt-primary)] font-bold">
                <Shield className="w-3.5 h-3.5" />
                Privacy &amp; power
              </div>
              <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-white/40 border border-white/15 rounded-full px-2 py-1">
                Sponsored
              </span>
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mb-1">
              Pair your client with the right plumbing
            </h2>
            <p className="text-sm text-[var(--tt-text-muted)] max-w-2xl mb-5 leading-relaxed">
              A client is just one piece. A VPN keeps your ISP out of your swarms; a seedbox keeps
              your home connection out of them entirely. Here&apos;s who we&apos;d hand the keys to.
            </p>

            <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">
              <Lock className="w-3 h-3" /> VPNs we trust
            </div>
            <div className="grid lg:grid-cols-2 gap-4 mb-6">
              {PARTNERS.vpn.map((p) => (
                <PartnerCard key={p.id} partner={p} />
              ))}
            </div>

            <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">
              <Server className="w-3 h-3" /> Seedbox providers
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              {PARTNERS.seedbox.map((p) => (
                <PartnerCard key={p.id} partner={p} />
              ))}
            </div>

            <p className="text-[11px] text-white/35 mt-4 font-mono-tt leading-relaxed">
              * Some links above are affiliate links. If you sign up through them TorHub may earn a
              small commission at no extra cost to you — it helps keep the lights on. We only list
              providers we&apos;d actually use ourselves.
            </p>
          </section>

          {/* FOOTNOTE */}
          <section className="mb-12 rounded-3xl border border-[var(--tt-border)] bg-gradient-to-br from-[rgba(255,42,109,0.06)] via-transparent to-[rgba(255,215,0,0.04)] backdrop-blur-md p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="hidden md:flex w-12 h-12 rounded-2xl bg-[var(--tt-primary)]/15 border border-[var(--tt-primary)]/30 items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[var(--tt-primary)]" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg md:text-xl font-extrabold mb-2">
                  A word on safety
                </h3>
                <p className="text-sm text-white/75 leading-relaxed">
                  Always download clients from their official sites — fake installers are the
                  number-one way malware sneaks onto torrent users&apos; machines. Bind your client to
                  your VPN interface so traffic stops the moment the tunnel drops, and respect the
                  laws of your jurisdiction. TorHub indexes metadata and magnets — what you do with
                  them is on you.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href="https://github.com/qbittorrent/qBittorrent"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-white/70 hover:text-white px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-white/30"
                    data-testid="software-source-qbittorrent"
                  >
                    <Github className="w-3.5 h-3.5" /> qBittorrent source
                  </a>
                  <a
                    href="https://github.com/transmission/transmission"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-white/70 hover:text-white px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-white/30"
                    data-testid="software-source-transmission"
                  >
                    <Github className="w-3.5 h-3.5" /> Transmission source
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  );
}
